import { Trans } from "@lingui/react/macro";
import { useState } from "react";
import { ConfirmationAction } from "@/components/ai-elements/confirmation";
import { logger } from "@/lib/logger";
import { UI_CONFIRM_NO } from "../shared/constants";
import {
  useApprovalSubmissionGuard,
  useLegacyToolApprovalPermission,
  useNativeToolApproval,
} from "./native-tool-approval";
import type { ConfirmProps } from "./types";

/**
 * Tool 実行の拒否を native / legacy の適切な応答経路へ送る。
 * approve と共有する排他 guard を通すことで、同じ Tool call への競合応答を防ぐ。
 */
export const DenyButton = ({
  toolCallId,
  toolName,
  addToolResult,
  sendMessage,
  children,
}: {
  toolCallId: string;
  toolName: string;
  addToolResult: ConfirmProps["addToolResult"];
  sendMessage: ConfirmProps["sendMessage"];
  children: React.ReactNode;
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionFailed, setSubmissionFailed] = useState(false);
  const nativeApproval = useNativeToolApproval();
  const canUseLegacyApproval = useLegacyToolApprovalPermission(
    nativeApproval === null,
  );
  const runApprovalSubmission = useApprovalSubmissionGuard();
  const canDeny = nativeApproval?.canRespond ?? canUseLegacyApproval;

  const handleDeny = async () => {
    if (!canDeny) return;
    await runApprovalSubmission(`${toolName}:${toolCallId}`, async () => {
      setIsSubmitting(true);
      setSubmissionFailed(false);
      try {
        if (nativeApproval) {
          await nativeApproval.addToolApprovalResponse({
            id: nativeApproval.approvalId,
            approved: false,
          });
          await sendMessage();
        } else {
          await addToolResult({
            toolCallId,
            tool: toolName,
            output: UI_CONFIRM_NO,
          });
          await sendMessage();
        }
      } catch (error) {
        setSubmissionFailed(true);
        logger.error(error, {
          component: "DenyButton",
          toolCallId,
          toolName,
        });
        throw error;
      } finally {
        setIsSubmitting(false);
      }
    });
  };

  return (
    <div className="space-y-2">
      <ConfirmationAction
        variant="outline"
        aria-describedby={nativeApproval?.responseDisabledReasonId}
        disabled={!canDeny || isSubmitting}
        onClick={() => void handleDeny().catch(() => undefined)}
      >
        {children}
      </ConfirmationAction>
      {submissionFailed ? (
        <p className="text-destructive text-xs" role="alert">
          <Trans>Could not submit the denial. Please try again.</Trans>
        </p>
      ) : null}
    </div>
  );
};
